import { invalidatePageDataCacheEntries } from "../hooks/usePageData.js";

const CHANNEL = "grain-order-data-change-v1";
const listeners = new Set();
let channel;
let revision = 0;

function receive(message, remote) {
  if (!["data-manager", "consolidation", "driver-portal", "dispatch"].includes(message?.source)) return;
  revision += 1;
  // Keep listening while either page is unmounted: its next load must be fresh.
  invalidatePageDataCacheEntries(["/api/data-manager/state"]);
  for (const listener of listeners) listener({ source: message.source, remote, revision });
}

function connect() {
  if (channel || typeof window === "undefined" || typeof BroadcastChannel === "undefined") return;
  try {
    channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = ({ data }) => receive(data, true);
  } catch { /* Same-tab invalidation still works when channels are unavailable. */ }
}

export function readOrderDataRevision() { return revision; }

export function publishOrderDataChange(source) {
  connect();
  receive({ source }, false);
  try { channel?.postMessage({ source }); } catch { /* Do not turn a saved mutation into a failure. */ }
}

export function subscribeOrderDataChange(listener) {
  connect();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// One-shot batching, never polling. A change during a read queues one fresh read.
export function createOrderRefreshQueue({ refresh, enabled, onError, delay = 60 }) {
  let pending = false, running = false, disposed = false, timer;
  const resume = () => {
    if (disposed || !pending || running || timer || !enabled()) return;
    timer = setTimeout(async () => {
      timer = undefined;
      if (disposed || !enabled()) return;
      pending = false;
      running = true;
      let failed = false;
      try { await refresh(); } catch (error) {
        failed = true;
        pending = true;
        if (!disposed) onError?.(error);
      } finally {
        running = false;
        if (!failed) resume();
      }
    }, delay);
  };
  return {
    invalidate() { pending = true; resume(); },
    hasPending: () => pending || running,
    resume,
    dispose() { disposed = true; clearTimeout(timer); },
  };
}
